import { Link } from "@tanstack/react-router";
import { SiteLayout } from "@/components/SiteLayout";
import kontorGruppe42 from "@/assets/kontor-gruppe-4-2.jpg";
import {
  ArrowRight,
  Check,
  CalendarRange,
  Users,
  AlertTriangle,
  ClipboardCheck,
  HardHat,
  type LucideIcon,
} from "lucide-react";
import { type Locale } from "@/i18n/config";
import { pathFor } from "@/i18n/slugMap";
import { BASE_URL, pageHead } from "@/i18n/head";
import { serviceSchema } from "@/lib/schema";
import { projektledelseStrings } from "@/i18n/content/projektledelse";
import { Img, SIZES } from "@/components/Img";

/** <head> for the project-management service page: pageHead + Service schema. */
export function projektledelseHead(locale: Locale) {
  const s = projektledelseStrings[locale];
  return pageHead("projektledelse", locale, s.meta, {
    breadcrumbName: s.hero.title,
    schema: [
      serviceSchema({
        name: s.hero.title,
        serviceType: s.hero.title,
        description: s.meta.description,
        url: BASE_URL + pathFor("projektledelse", locale),
        image: s.meta.ogImage,
      }),
    ],
  });
}

// Icons aligned by index with the value-prop and phase arrays in the catalog.
const valuePropIcons: LucideIcon[] = [CalendarRange, Users, AlertTriangle];
const phaseIcons: LucideIcon[] = [ClipboardCheck, HardHat];

export function ProjektledelsePage({ locale }: { locale: Locale }) {
  const t = projektledelseStrings[locale];

  return (
    <SiteLayout>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <Img
            src={kontorGruppe42}
            alt={t.hero.title}
            className="h-full w-full object-cover"
            sizes={SIZES.full}
            priority
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/85 to-background/40" />
        </div>
        <div className="relative mx-auto max-w-7xl px-4 md:px-8 py-24 md:py-36">
          <p className="text-sm uppercase tracking-widest text-primary font-semibold">{t.hero.eyebrow}</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-bold tracking-tight max-w-3xl">{t.hero.title}</h1>
          <p className="mt-6 text-lg md:text-xl text-foreground/85 leading-relaxed max-w-2xl">{t.hero.body}</p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to={pathFor("kontakt", locale)} className="btn-cta">
              {t.hero.ctaPrimary} <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to={pathFor("ydelser", locale)} className="inline-flex items-center gap-2 rounded-full border border-white/20 px-8 py-3.5 text-base font-semibold text-foreground hover:bg-white/5">
              {t.hero.ctaSecondary}
            </Link>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 md:py-24">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 md:grid-cols-2 items-center">
          <div>
            <p className="text-sm uppercase tracking-widest text-foreground/70">{t.intro.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.intro.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.intro.p1}</p>
          </div>
          <Img
            src="/wp-content/uploads/Kontor-gruppe-15_1400x1400.jpg"
            alt={t.intro.heading}
            className="rounded-2xl w-full aspect-[4/3] object-cover"
            sizes={SIZES.split}
          />
        </div>
      </section>

      {/* Value props */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest opacity-70">{t.why.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.why.heading}</h2>
            <p className="mt-4 text-lg leading-relaxed opacity-90">{t.why.intro}</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {t.valueProps.map((v, i) => {
              const Icon = valuePropIcons[i] ?? CalendarRange;
              return (
                <div key={v.title} className="rounded-2xl bg-background/5 border border-foreground/10 p-8 backdrop-blur">
                  <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-primary/15 text-primary">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-xl font-bold">{v.title}</h3>
                  <p className="mt-3 leading-relaxed opacity-90">{v.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Phases */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="max-w-3xl">
            <p className="text-sm uppercase tracking-widest text-foreground/70">{t.phasesHead.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.phasesHead.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.phasesHead.intro}</p>
          </div>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {t.phases.map((p, i) => {
              const Icon = phaseIcons[i] ?? ClipboardCheck;
              return (
                <article key={p.title} className="rounded-3xl bg-secondary/60 border border-white/5 p-8 hover:shadow-2xl hover:shadow-primary/5 transition-shadow">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold uppercase tracking-widest text-primary">{p.label}</span>
                    <Icon className="h-6 w-6 text-foreground/60" />
                  </div>
                  <h3 className="mt-4 text-2xl font-bold">{p.title}</h3>
                  <p className="mt-4 text-foreground/80 leading-relaxed">{p.intro}</p>
                  <ul className="mt-6 space-y-3">
                    {p.items.map((it) => (
                      <li key={it} className="flex gap-3 text-foreground/90 leading-relaxed">
                        <span className="mt-1 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
                          <Check className="h-3 w-3" />
                        </span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* AB18 */}
      <section className="py-20 bg-secondary/30">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 md:grid-cols-2 items-center">
          <Img
            src="/wp-content/uploads/MISupportIT_NTK4202_1350x900.jpg"
            alt={t.ab18.heading}
            className="rounded-2xl w-full aspect-[4/3] object-cover"
            sizes={SIZES.split}
          />
          <div>
            <p className="text-sm uppercase tracking-widest text-foreground/70">{t.ab18.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.ab18.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.ab18.p1}</p>
            <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{t.ab18.p2}</p>
            <Link to={pathFor("kontakt", locale)} className="mt-8 btn-cta">
              {t.ab18.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Pharma */}
      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-4 md:px-8 grid gap-12 md:grid-cols-2 items-center">
          <div>
            <p className="text-sm uppercase tracking-widest text-foreground/70">{t.pharma.eyebrow}</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold tracking-tight">{t.pharma.heading}</h2>
            <p className="mt-6 text-lg text-foreground/85 leading-relaxed">{t.pharma.p1}</p>
            <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{t.pharma.p2}</p>
            <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{t.pharma.p3}</p>
            <p className="mt-4 text-lg text-foreground/85 leading-relaxed">{t.pharma.p4}</p>
            <Link to={pathFor("pharma", locale)} className="mt-8 btn-cta">
              {t.pharma.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="rounded-3xl bg-secondary/60 border border-white/5 p-10">
            <p className="text-xl md:text-2xl italic leading-relaxed text-foreground/90">
              "{t.pharma.quote}"
            </p>
            <p className="mt-6 text-sm uppercase tracking-widest text-foreground/70">
              {t.pharma.quoteAttrib}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-section-light text-section-light-foreground py-20">
        <div className="mx-auto max-w-4xl px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">{t.cta.heading}</h2>
          <p className="mt-6 text-lg leading-relaxed">{t.cta.body}</p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <a
              href="tel:+4555551212"
              className="inline-flex items-center gap-2 rounded-2xl border-2 border-secondary bg-transparent text-secondary px-9 py-4 text-base font-semibold transition-colors hover:bg-secondary hover:text-white"
            >
              {t.cta.call}
            </a>
            <Link to={pathFor("kontakt", locale)} className="btn-cta">
              {t.cta.write} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
